import React from "react";
import styled from "styled-components";
import Tooltip from "@mui/material/Tooltip";
import { TextEntryFormControl } from "../TextEntryFormControl";
import { TextControlStyle } from "../../Utilities/FormDesignConstants";
import { StyledFormHelperText, StyledTextField } from "./CommonStyled";
import { IssueData } from "../../Utilities/ControlRenderContext";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { StringDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { ValidationIssue } from "../../../rule-engine-core/Common/IDesignValidationProvider";
import { IconButton } from "@mui/material";
import { PropertyEditorBoolean } from "../../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { PropertyEditorInteger } from "../../../CommonUI/PropertyEditing/PropertyEditorInteger";
import { ShowIcon } from "../../../CommonUI/Icon/ShowIcon";
import { HideIcon } from "../../../CommonUI/Icon/HideIcon";
import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { PropertyEditorCollapse } from "../../../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

export class PasswordFormControl extends TextEntryFormControl {
    readonly __type: string = "Password";

    // options
    requireMinLength: boolean = false;
    requireUpperCaseAndLowerCase: boolean = false;
    requireNumber: boolean = false;
    requireSymbol: boolean = false;
    confirmPasswordRequired: boolean = false;
    minimumLength: number = 3;

    confirmPassword: string = "";
    isMask: boolean = true;

    gapWithConfirmPassword: number = 10;

    getRuntimeValidationIssues(): ValidationIssue[] {
        const runtimeIssues = super.getRuntimeValidationIssues();
        const validationPasswordIssues = this.validatePassword();
        if (validationPasswordIssues) {
            for (let val of validationPasswordIssues) {
                runtimeIssues.push(new ValidationIssue(val, this.valueName, this.id));
            }
        }

        return runtimeIssues;
    }

    render() {
        return (
            <ObserveState
                listenTo={this}
                properties={["isMask", "confirmPassword"]}
                control={() => {
                    let style = this.getFormContext()?.form?.defaultTextStyle;

                    if (this.overrideStyle && this.style) style = this.style;

                    let size: "medium" | "small" = this.getFormContext()?.form?.defaultTextSize ?? "medium";

                    if (this.overrideStyle && this.size) size = this.size;

                    const issueData: IssueData | null = this.getFormContext()!
                        .getControlContext(this)!
                        .getRuntimeIssueData();
                    const issueText: string = issueData?.text || "";
                    const customWidth = this.fullWidth ? "100%" : this.width ? `${this.width}px` : "200px";
                    let content: JSX.Element | null = null;

                    switch (style) {
                        case TextControlStyle.LABELED:
                            content = (
                                <>
                                    <StyledTextFieldWrap width={customWidth}>
                                        {this.label && <UILabel variant={"form_label"}>{this.label}</UILabel>}
                                        <StyledTextField
                                            type={this.isMask ? "password" : "text"}
                                            size={size}
                                            data-size={size}
                                            data-testid={this.valueName}
                                            variant={"outlined"}
                                            fullWidth={this.fullWidth}
                                            width={customWidth}
                                            value={this.getValue()}
                                            onChange={this.handleChange}
                                            error={Boolean(issueText)}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        onClick={this.showPassword}
                                                        size="large"
                                                    >
                                                        {this.isMask ? <ShowIcon /> : <HideIcon />}
                                                    </IconButton>
                                                ),
                                                inputRef: this.ref,
                                            }}
                                        />
                                        {issueText && issueText.length > 30 && (
                                            <Tooltip
                                                title={issueText}
                                                arrow
                                                placement="bottom"
                                            >
                                                <StyledFormHelperText
                                                    error={Boolean(issueText)}
                                                    data-testid={`${this.valueName || "textfield"}-hinttext`}
                                                >
                                                    {issueText.slice(0, 30) + "..."}
                                                </StyledFormHelperText>
                                            </Tooltip>
                                        )}
                                        {((issueText && issueText.length < 31) || this.hintText) && (
                                            <StyledFormHelperText
                                                error={Boolean(issueText)}
                                                data-testid={`${this.valueName || "textfield"}-hinttext`}
                                            >
                                                {issueText ? issueText : this.hintText}
                                            </StyledFormHelperText>
                                        )}
                                    </StyledTextFieldWrap>
                                    {this.confirmPasswordRequired && (
                                        <StyledTextFieldWrap
                                            width={customWidth}
                                            marginTop={this.gapWithConfirmPassword}
                                        >
                                            {this.label && (
                                                <UILabel variant={"form_label"}>Confirm {this.label}</UILabel>
                                            )}
                                            <StyledTextField
                                                type={this.isMask ? "password" : "text"}
                                                size={size}
                                                data-size={size}
                                                data-testid={`${this.valueName || "textfield"}-confirm`}
                                                variant={"outlined"}
                                                fullWidth={this.fullWidth}
                                                width={customWidth}
                                                value={this.confirmPassword}
                                                onChange={this.handleConfirmPasswordChange}
                                                error={Boolean(issueText)}
                                                InputProps={{
                                                    inputRef: this.ref,
                                                }}
                                            />
                                            {issueText && issueText.length > 30 && (
                                                <Tooltip
                                                    title={issueText}
                                                    arrow
                                                    placement="bottom"
                                                >
                                                    <StyledFormHelperText
                                                        error={Boolean(issueText)}
                                                        data-testid={`${this.valueName || "textfield"}-hinttext`}
                                                    >
                                                        {issueText.slice(0, 30) + "..."}
                                                    </StyledFormHelperText>
                                                </Tooltip>
                                            )}
                                            {((issueText && issueText.length < 31) || this.hintText) && (
                                                <StyledFormHelperText
                                                    error={Boolean(issueText)}
                                                    data-testid={`${this.valueName || "textfield"}-hinttext`}
                                                >
                                                    {issueText ? issueText : this.hintText}
                                                </StyledFormHelperText>
                                            )}
                                        </StyledTextFieldWrap>
                                    )}
                                </>
                            );
                            break;
                        case TextControlStyle.SHADED:
                            content = (
                                <>
                                    <StyledTextFieldWrap width={customWidth}>
                                        <StyledTextField
                                            size={size}
                                            type={this.isMask ? "password" : "text"}
                                            data-size={size}
                                            data-testid={this.valueName}
                                            variant={"filled"}
                                            customstyle={"filled"}
                                            fullWidth={this.fullWidth}
                                            width={customWidth}
                                            value={this.getValue()}
                                            onChange={this.handleChange}
                                            error={Boolean(issueText)}
                                            label={this.label ? this.label : this.valueName}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        onClick={this.showPassword}
                                                        size="large"
                                                    >
                                                        {this.isMask ? <ShowIcon /> : <HideIcon />}
                                                    </IconButton>
                                                ),
                                                inputRef: this.ref,
                                            }}
                                        />
                                        {(issueText || this.hintText) && (
                                            <StyledFormHelperText
                                                error={Boolean(issueText)}
                                                data-testid={`${this.valueName || "textfield"}-hinttext`}
                                            >
                                                {issueText ? issueText : this.hintText}
                                            </StyledFormHelperText>
                                        )}
                                    </StyledTextFieldWrap>
                                    {this.confirmPasswordRequired && (
                                        <StyledTextFieldWrap
                                            width={customWidth}
                                            marginTop={this.gapWithConfirmPassword}
                                        >
                                            <StyledTextField
                                                size={size}
                                                type={this.isMask ? "password" : "text"}
                                                data-size={size}
                                                data-testid={`${this.valueName || "textfield"}-confirm`}
                                                variant={"filled"}
                                                customstyle={"filled"}
                                                fullWidth={this.fullWidth}
                                                width={customWidth}
                                                value={this.confirmPassword}
                                                onChange={this.handleConfirmPasswordChange}
                                                error={Boolean(issueText)}
                                                label={`Confirm ${this.label ? this.label : this.valueName}`}
                                                InputProps={{
                                                    inputRef: this.ref,
                                                }}
                                            />

                                            {(issueText || this.hintText) && (
                                                <StyledFormHelperText
                                                    error={Boolean(issueText)}
                                                    data-testid={`${this.valueName || "textfield"}-hinttext`}
                                                >
                                                    {issueText ? issueText : this.hintText}
                                                </StyledFormHelperText>
                                            )}
                                        </StyledTextFieldWrap>
                                    )}
                                </>
                            );
                            break;
                        case TextControlStyle.UNDERLINED:
                            content = (
                                <>
                                    <StyledTextFieldWrap width={customWidth}>
                                        <StyledTextField
                                            size={size}
                                            type={this.isMask ? "password" : "text"}
                                            data-size={size}
                                            data-testid={this.valueName}
                                            variant={"standard"}
                                            customstyle={"standard"}
                                            fullWidth={this.fullWidth}
                                            width={customWidth}
                                            value={this.getValue()}
                                            onChange={this.handleChange}
                                            error={Boolean(issueText)}
                                            label={this.label ? this.label : this.valueName}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        onClick={this.showPassword}
                                                        size="large"
                                                    >
                                                        {this.isMask ? <ShowIcon /> : <HideIcon />}
                                                    </IconButton>
                                                ),
                                                inputRef: this.ref,
                                            }}
                                        />
                                        {(issueText || this.hintText) && (
                                            <StyledFormHelperText
                                                error={Boolean(issueText)}
                                                data-testid={`${this.valueName || "textfield"}-hinttext`}
                                            >
                                                {issueText ? issueText : this.hintText}
                                            </StyledFormHelperText>
                                        )}
                                    </StyledTextFieldWrap>
                                    {this.confirmPasswordRequired && (
                                        <StyledTextFieldWrap
                                            width={customWidth}
                                            marginTop={this.gapWithConfirmPassword}
                                        >
                                            <StyledTextField
                                                size={size}
                                                type={this.isMask ? "password" : "text"}
                                                data-size={size}
                                                data-testid={`${this.valueName || "textfield"}-confirm`}
                                                variant={"standard"}
                                                customstyle={"standard"}
                                                fullWidth={this.fullWidth}
                                                width={customWidth}
                                                value={this.confirmPassword}
                                                onChange={this.handleConfirmPasswordChange}
                                                error={Boolean(issueText)}
                                                label={`Confirm ${this.label ? this.label : this.valueName}`}
                                                InputProps={{
                                                    inputRef: this.ref,
                                                }}
                                            />

                                            {(issueText || this.hintText) && (
                                                <StyledFormHelperText
                                                    error={Boolean(issueText)}
                                                    data-testid={`${this.valueName || "textfield"}-hinttext`}
                                                >
                                                    {issueText ? issueText : this.hintText}
                                                </StyledFormHelperText>
                                            )}
                                        </StyledTextFieldWrap>
                                    )}
                                </>
                            );
                            break;
                        case TextControlStyle.OUTLINE:
                            content = (
                                <>
                                    <StyledTextFieldWrap width={customWidth}>
                                        <StyledTextField
                                            size={size}
                                            type={this.isMask ? "password" : "text"}
                                            data-size={size}
                                            data-testid={this.valueName}
                                            variant={"outlined"}
                                            customstyle={"outlined"}
                                            fullWidth={this.fullWidth}
                                            width={customWidth}
                                            value={this.getValue()}
                                            onChange={this.handleChange}
                                            error={Boolean(issueText)}
                                            label={this.label ? this.label : this.valueName}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        onClick={this.showPassword}
                                                        size="large"
                                                    >
                                                        {this.isMask ? <ShowIcon /> : <HideIcon />}
                                                    </IconButton>
                                                ),
                                                inputRef: this.ref,
                                            }}
                                        />
                                        {(issueText || this.hintText) && (
                                            <StyledFormHelperText
                                                error={Boolean(issueText)}
                                                data-testid={`${this.valueName || "textfield"}-hinttext`}
                                            >
                                                {issueText ? issueText : this.hintText}
                                            </StyledFormHelperText>
                                        )}
                                    </StyledTextFieldWrap>
                                    {this.confirmPasswordRequired && (
                                        <StyledTextFieldWrap
                                            width={customWidth}
                                            marginTop={this.gapWithConfirmPassword}
                                        >
                                            <StyledTextField
                                                size={size}
                                                type={this.isMask ? "password" : "text"}
                                                data-size={size}
                                                data-testid={`${this.valueName || "textfield"}-confirm`}
                                                variant={"outlined"}
                                                customstyle={"outlined"}
                                                fullWidth={this.fullWidth}
                                                width={customWidth}
                                                value={this.confirmPassword}
                                                onChange={this.handleConfirmPasswordChange}
                                                error={Boolean(issueText)}
                                                label={`Confirm ${this.label ? this.label : this.valueName}`}
                                                InputProps={{
                                                    inputRef: this.ref,
                                                }}
                                            />

                                            {(issueText || this.hintText) && (
                                                <StyledFormHelperText
                                                    error={Boolean(issueText)}
                                                    data-testid={`${this.valueName || "textfield"}-hinttext`}
                                                >
                                                    {issueText ? issueText : this.hintText}
                                                </StyledFormHelperText>
                                            )}
                                        </StyledTextFieldWrap>
                                    )}
                                </>
                            );
                            break;
                    }

                    return <>{content}</>;
                }}
            />
        );
    }

    renderEditUI(): JSX.Element | null {
        return <XPasswordBoxEditUI editMe={this} />;
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.valueName) return;

        let def = new StringDataDefinition();
        def.name = this.valueName;
        return def;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(
            this,
            retData,
            "__type",
            "requireMinLength",
            "requireUpperCaseAndLowerCase",
            "requireNumber",
            "requireSymbol",
            "confirmPasswordRequired",
            "minimumLength",
            "confirmPassword",
            "isMask",
            "gapWithConfirmPassword",
        );

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(
            this,
            data,
            "__type",
            "requireMinLength",
            "requireUpperCaseAndLowerCase",
            "requireNumber",
            "requireSymbol",
            "confirmPasswordRequired",
            "minimumLength",
            "confirmPassword",
            "isMask",
            "gapWithConfirmPassword",
        );
    }

    private showPassword = () => {
        this.isMask = !this.isMask;
        StateManager.propertyChanged(this, "isMask");
    };

    private handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (this.valueName) {
            this.setFormDataValue(this.valueName, event.currentTarget.value);
        }
    };

    private getValue = () => {
        return this.valueName ? this.getFormDataValue(this.valueName) : "";
    };

    private handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (this.getValue() && this.getValue() !== "") {
            this.confirmPassword = event.currentTarget.value;
            StateManager.propertyChanged(this, "confirmPassword");

            // todo: fix
            this.setFormDataValue(this.valueName!, this.getValue());
        }
    };

    private validatePassword = () => {
        let passwordValue = this.valueName ? this.getFormDataValue(this.valueName) : "";
        if (this.valueName && passwordValue) {
            const errorMessages: string[] = [];
            if (this.requireMinLength === true) {
                if (passwordValue.length < this.minimumLength) {
                    errorMessages.push(`Required ${this.minimumLength} character.`);
                }
            }
            if (this.requireUpperCaseAndLowerCase === true) {
                if (passwordValue.search(/[A-Z a-z]/) === -1) {
                    return "Required Upper & Lower Case";
                }
            }
            if (this.requireSymbol === true) {
                if (passwordValue.search(/[!/@/#/$/%/6/&/*/(/)/_/+/./,/;/:]/) === -1) {
                    errorMessages.push("Required symbol");
                }
            }
            if (this.requireNumber === true) {
                if (passwordValue.search(/[0-9]/) === -1) {
                    errorMessages.push("Required numeric value");
                }
            }
            if (this.confirmPasswordRequired) {
                if (passwordValue !== this.confirmPassword) {
                    errorMessages.push("Passwords does not match");
                }
            }

            return errorMessages;
        }
    };
}

function XPasswordBoxEditUI(props: { editMe: PasswordFormControl }) {
    return (
        <UISpacedBoxContent>
            {props.editMe.renderBaseDataControlEditor()}
            {props.editMe.renderTextControlBasePropertyEditor()}
            {props.editMe.renderTextStyleSelectionEditor()}

            <PropertyEditorBoolean
                label={"Confirm Password"}
                propertyName={"confirmPasswordRequired"}
                editObject={props.editMe}
            />

            <UILabel variant={"form_label"}>Password rules</UILabel>

            <PropertyEditorBoolean
                label={"Require Upper and Lower Case"}
                propertyName={"requireUpperCaseAndLowerCase"}
                editObject={props.editMe}
            />

            <PropertyEditorBoolean
                label={"Require Number"}
                propertyName={"requireNumber"}
                editObject={props.editMe}
            />

            <PropertyEditorBoolean
                label={"Require Symbol"}
                propertyName={"requireSymbol"}
                editObject={props.editMe}
            />

            <PropertyEditorBoolean
                label={"Minimum Length"}
                propertyName={"requireMinLength"}
                editObject={props.editMe}
            />

            <PropertyEditorCollapse
                editObject={props.editMe}
                collapseLogicOrField={"requireMinLength"}
            >
                <PropertyEditorInteger
                    label={"Minimum Length"}
                    propertyName={"minimumLength"}
                    editObject={props.editMe}
                />
            </PropertyEditorCollapse>
        </UISpacedBoxContent>
    );
}

const StyledTextFieldWrap = styled.div<{ width?: string; marginTop?: number }>`
    width: ${({ width }) => width} !important;
    height: 100% !important;
    box-sizing: border-box !important;
    position: relative !important;

    margin-top: ${({ marginTop }) => marginTop || 0}px;
`;
